import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getDemoClientId, isDemoMode } from "@/lib/demoMode";

/**
 * Resolves the currently logged-in user's OWN client_id fresh from auth.uid().
 * Never relies on props or cached state. Prevents cross-client data leaks.
 *
 * In demo mode (see src/lib/demoMode.ts), returns the configured demo client_id
 * without calling auth — so /demo works fully unauthenticated.
 */
export function useOwnClientId(componentName: string) {
  const [ownClientId, setOwnClientId] = useState<string | null>(
    isDemoMode() ? getDemoClientId() : null,
  );
  const [resolved, setResolved] = useState(isDemoMode());

  useEffect(() => {
    if (isDemoMode()) {
      setOwnClientId(getDemoClientId());
      setResolved(true);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth.user?.id;
      if (!uid) {
        if (!cancelled) {
          setOwnClientId(null);
          setResolved(true);
        }
        return;
      }
      const { data } = await supabase
        .from("clients")
        .select("id,user_id")
        .eq("user_id", uid)
        .order("created_at", { ascending: true })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      const id = data && data.user_id === uid ? (data.id as string) : null;
      console.log(`[${componentName}] resolved own client_id:`, id, "uid:", uid);
      setOwnClientId(id);
      setResolved(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [componentName]);

  return { ownClientId, resolved };
}
